import {
  Box,
  Modal,
  Typography,
  FormControl,
  Button,
  TextField,
  Select,
  MenuItem,
  List,
  ListItem,
  OutlinedInput,
  Chip,
  ListItemText,
  Autocomplete,
  useMediaQuery,
  Checkbox
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactComponent as CheckBoxOutlineIcon } from '../../../icons/checkBoxOutlineIcon.svg';
import { ReactComponent as CheckedIcon } from '../../../icons/checkBoxIcon.svg';
import SwitchButton from './SwitchButton';
import {
  AddOrganizationData,
  OrganizationDefaultSettingById,
  UpdateOrganizationDetailById
} from '../../../network/Organization';
import { PsmFrequencyData } from '../../../utils/Utilities';

const useStyles = makeStyles((theme) => ({
  customDashCard: {
    background: '#FFFFFF',
    boxShadow: '10px 10px 19px 1px #EAEAFF',
    borderRadius: '20px',
    minHeight: '190px'
  },
  textStyle: {
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '18px',
    textAlign: 'left',
    color: '#7c7d7d'
  },
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  }
}));

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: { xs: '70%', lg: '40%' },
  maxWidth: { xs: '70%', lg: '40%' },
  width: '50%',
  bgcolor: 'background.paper',
  py: 2,
  px: 2,
  borderRadius: 1,
  maxHeight: '90vh',
  overflowY: 'auto'
};

const addOrganizationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  address: Yup.string().required('Required'),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits')
    .required('Required')
  // countryCode: Yup.string().required('Required')
});

export default function AddNewOrganization({
  openAssignedModal,
  handleClose,
  data,
  onAction,
  setAlertMetas,
  alertMetas
}) {
  const unlockChannelMethods = ['Dashboard', 'RFID', 'SMS'];
  const [createUserStepNo, setCreateUserStepNo] = useState('1');
  const [organisationSettingData, setOrganisationSettingData] = useState({});
  const [organisationDeviceData, setOrganisationDeviceData] = useState({
    delayedTripThreshold: 0,
    lowBatteryThreshold: 0,
    geoFenceRadius: 0,
    notificationFrequency: 0,
    isShipmentDelayAllowed: false,
    vipNumbers: [],
    selectedUnlockChannel: ['Dashboard', 'RFID', 'SMS'],
    unlock_channel_MQTT: true,
    unlock_channel_SMS: true,
    unlock_channel_RFID: true,
    isPowerSwitchEnabled: false,
    psm_freq: 0
  });

  const [listDense, setListDense] = useState(false);
  const classes = useStyles();

  const headerText =
    (createUserStepNo === '2' && "Set organisation's device settings") ||
    (data?.id && 'Update Organization Details') ||
    'Create New Organization';

  const [vipNumberError, setVipNumberError] = useState(false);
  const [vipLengthError, setVipLengthError] = useState(false);

  const addNewOrganizationFormik = useFormik({
    initialValues: {
      name: '',
      email: '',
      address: '',
      phone: '',
      countryCode: '91'
    },
    validationSchema: addOrganizationSchema,
    onSubmit: (values, { resetForm }) => {
      addNewOrganizationFormik.isSubmitting = true;
      const payload = {
        email: values?.email,
        name: values?.name,
        address: values?.address,
        phone: values?.phone,
        isActive: true,
        countryCode: values?.countryCode,
        settings: {
          delayedTripThreshold: organisationDeviceData?.delayedTripThreshold,
          isShipmentDelayAllowed: organisationDeviceData?.isShipmentDelayAllowed,
          geoFenceRadius: organisationDeviceData?.geoFenceRadius,
          vipNumbers: organisationDeviceData?.vipNumbers,
          lowBatteryThreshold: organisationDeviceData?.lowBatteryThreshold,
          notificationFrequency: organisationDeviceData?.notificationFrequency,
          fwVersion: organisationSettingData?.fwVersion,
          communicationMode: organisationSettingData?.communicationMode,
          psm_freq: organisationSettingData?.psm_freq,
          normal_mode: organisationSettingData?.normal_mode,
          standby_mode: organisationSettingData?.standby_mode,
          unlock_channel_MQTT: organisationDeviceData?.unlock_channel_MQTT,
          unlock_channel_SMS: organisationDeviceData?.unlock_channel_SMS,
          unlock_channel_RFID: organisationDeviceData?.unlock_channel_RFID,
          isPowerSwitchEnabled: organisationDeviceData?.isPowerSwitchEnabled,
          mqtt_host: organisationSettingData?.mqtt_host,
          mqtt_port: organisationSettingData?.mqtt_port,
          mqtt_username: organisationSettingData?.mqtt_username,
          mqtt_password: organisationSettingData?.mqtt_password,
          passwordHash: organisationSettingData?.passwordHash
        }
      };
      addNewOrganization(payload);
      resetForm();
    }
  });

  const { errors, touched, values, handleSubmit, getFieldProps } = addNewOrganizationFormik;

  const addNewOrganization = async (payload) => {
    try {
      const res = data?.id
        ? await UpdateOrganizationDetailById(data?.id, payload)
        : await AddOrganizationData(payload);
      if (res) {
        setAlertMetas({
          ...alertMetas,
          severity: 'success',
          snackBarOpened: true,
          msg: data?.id
            ? 'Organization Updated Successfully !'
            : 'Organization Created Successfully !'
        });
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message
      });
    } finally {
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
      handleClose();
      onAction();
      setCreateUserStepNo('1');
    }
  };

  const getOrganisationSettingData = async () => {
    try {
      const res = await OrganizationDefaultSettingById();
      if (res) {
        setOrganisationSettingData(res?.data?.data);
      }
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrganisationDeviceData({ ...organisationDeviceData, [name]: value });
  };

  const handleSwitchStatus = (e, name) => {
    setOrganisationDeviceData({ ...organisationDeviceData, [name]: e.target.checked });
  };

  useEffect(() => {
    if (!data.id) {
      getOrganisationSettingData();
    }
  }, []);

  const getHelperText = () => {
    if (vipNumberError) return 'You can add only up to 4 numbers.';
    if (vipLengthError) return 'Invalid number';
    return '';
  };

  const isSmallScreen = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    if (data?.id) {
      addNewOrganizationFormik.setFieldValue('name', data?.name);
      addNewOrganizationFormik.setFieldValue('email', data?.email);
      addNewOrganizationFormik.setFieldValue('address', data?.address);
      addNewOrganizationFormik.setFieldValue('phone', data?.phone);
      addNewOrganizationFormik.setFieldValue('countryCode', data?.countryCode);
      const selectedChannels = [];
      if (data?.settings?.unlock_channel_MQTT) selectedChannels.push('Dashboard');
      if (data?.settings?.unlock_channel_SMS) selectedChannels.push('SMS');
      if (data?.settings?.unlock_channel_RFID) selectedChannels.push('RFID');
      setOrganisationDeviceData({
        delayedTripThreshold: data?.settings?.delayedTripThreshold,
        lowBatteryThreshold: data?.settings?.lowBatteryThreshold,
        geoFenceRadius: data?.settings?.geoFenceRadius,
        notificationFrequency: data?.settings?.notificationFrequency,
        isShipmentDelayAllowed: data?.settings?.isShipmentDelayAllowed,
        vipNumbers: data?.settings?.vipNumbers || [],
        selectedUnlockChannel: selectedChannels,
        isPowerSwitchEnabled: data?.settings?.isPowerSwitchEnabled,
        psm_freq: data?.settings?.psm_freq
      });
    } else {
      const selectedChannels = [];
      if (organisationSettingData?.unlock_channel_MQTT) selectedChannels.push('Dashboard');
      if (organisationSettingData?.unlock_channel_SMS) selectedChannels.push('SMS');
      if (organisationSettingData?.unlock_channel_RFID) selectedChannels.push('RFID');
      setOrganisationDeviceData({
        delayedTripThreshold: organisationSettingData?.delayedTripThreshold,
        lowBatteryThreshold: organisationSettingData?.lowBatteryThreshold,
        geoFenceRadius: organisationSettingData?.geoFenceRadius,
        notificationFrequency: organisationSettingData?.notificationFrequency,
        isShipmentDelayAllowed: organisationSettingData?.isShipmentDelayAllowed,
        vipNumbers: organisationSettingData?.vipNumbers || [],
        unlock_channel_MQTT: organisationSettingData?.unlock_channel_MQTT,
        unlock_channel_RFID: organisationSettingData?.unlock_channel_RFID,
        unlock_channel_SMS: organisationSettingData?.unlock_channel_SMS,
        selectedUnlockChannel: selectedChannels,
        psm_freq: organisationSettingData?.psm_freq,
        isPowerSwitchEnabled: organisationSettingData?.isPowerSwitchEnabled
      });
    }
  }, [organisationSettingData, data]);

  useEffect(() => {
    setOrganisationDeviceData((prevState) => ({
      ...prevState,
      unlock_channel_RFID: organisationDeviceData?.selectedUnlockChannel.includes('RFID'),
      unlock_channel_MQTT: organisationDeviceData?.selectedUnlockChannel.includes('Dashboard'),
      unlock_channel_SMS: organisationDeviceData?.selectedUnlockChannel.includes('SMS')
    }));
  }, [organisationDeviceData.selectedUnlockChannel]);

  console.log(organisationDeviceData, 'organisationDeviceData');

  return (
    <Box>
      <Modal
        open={openAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        data-testid="add-new-organisation"
      >
        <Box sx={style}>
          <Typography sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 900 }}>
            {headerText}
          </Typography>

          <FormikProvider value={addNewOrganizationFormik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              {createUserStepNo === '1' && (
                <Box>
                  <FormControl data-testid="organisation-name" fullWidth sx={{ mt: 3, mb: -2 }}>
                    <TextField
                      label={
                        <Typography
                          sx={{
                            fontSize: {
                              xs: '12px',
                              sm: '14px'
                            },
                            fontWeight: 500
                          }}
                        >
                          Name *
                        </Typography>
                      }
                      sx={{
                        '& .MuiOutlinedInput-input': {
                          fontSize: { xs: '12px', sm: '14px' },
                          padding: { xs: '14px 8px', sm: '16.5px' }
                        }
                      }}
                      id="name"
                      name="name"
                      variant="outlined"
                      {...getFieldProps('name')}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </FormControl>
                  <FormControl data-testid="organisation-email" fullWidth sx={{ mt: 3, mb: -2 }}>
                    <TextField
                      label={
                        <Typography
                          sx={{
                            fontSize: {
                              xs: '12px',
                              sm: '14px'
                            },
                            fontWeight: 500
                          }}
                        >
                          Email *
                        </Typography>
                      }
                      sx={{
                        '& .MuiOutlinedInput-input': {
                          fontSize: { xs: '12px', sm: '14px' },
                          padding: { xs: '14px 8px', sm: '16.5px' }
                        }
                      }}
                      id="email"
                      name="email"
                      variant="outlined"
                      {...getFieldProps('email')}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </FormControl>
                  <FormControl data-testid="organisation-phone" fullWidth sx={{ mt: 3, mb: -2 }}>
                    <TextField
                      type="text"
                      label={
                        <Typography
                          sx={{
                            fontSize: {
                              xs: '12px',
                              sm: '14px'
                            },
                            fontWeight: 500
                          }}
                        >
                          Mobile Number *
                        </Typography>
                      }
                      sx={{
                        '& .MuiOutlinedInput-input': {
                          fontSize: { xs: '12px', sm: '14px' },
                          padding: { xs: '14px 8px', sm: '16.5px' }
                        }
                      }}
                      id="phone"
                      name="phone"
                      variant="outlined"
                      {...getFieldProps('phone')}
                      error={Boolean(touched.phone && errors.phone)}
                      helperText={touched.phone && errors.phone}
                    />
                  </FormControl>
                  <FormControl data-testid="organisation-address" fullWidth sx={{ mt: 3 }}>
                    <TextField
                      label={
                        <Typography
                          sx={{
                            fontSize: {
                              xs: '12px',
                              sm: '14px'
                            },
                            fontWeight: 500
                          }}
                        >
                          Address *
                        </Typography>
                      }
                      sx={{
                        '& .MuiOutlinedInput-input': {
                          fontSize: { xs: '12px', sm: '14px' },
                          padding: { xs: '14px 8px', sm: '16.5px' }
                        }
                      }}
                      id="address"
                      name="address"
                      variant="outlined"
                      {...getFieldProps('address')}
                      error={Boolean(touched.address && errors.address)}
                      helperText={touched.address && errors.address}
                    />
                  </FormControl>{' '}
                  <Box sx={{ display: 'flex', float: 'right', flexWrap: 'wrap', mt: 3 }}>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ mr: 2, boxShadow: 'none' }}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ boxShadow: 'none' }}
                      onClick={() => setCreateUserStepNo('2')}
                      disabled={Boolean(
                        !addNewOrganizationFormik.values.name ||
                          !addNewOrganizationFormik.values.email ||
                          !addNewOrganizationFormik.values.address ||
                          !addNewOrganizationFormik.values.phone ||
                          (touched.name && errors.name) ||
                          (touched.email && errors.email) ||
                          (touched.address && errors.address) ||
                          (touched.phone && errors.phone)
                      )}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}

              {createUserStepNo === '2' && (
                <Box sx={{ mt: 2 }}>
                  <List dense={listDense} sx={{ p: 0 }}>
                    <ListItem
                      sx={{
                        py: { xs: 1, sm: 2 },
                        px: { xs: 0.5, sm: 2 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'flex-start',
                        gap: 1,
                        backgroundColor: 'rgba(176, 185, 199, .1)'
                      }}
                    >
                      <ListItemText
                        primary="Unlock Channel"
                        sx={{ mt: { xs: 0, sm: 1 } }}
                        primaryTypographyProps={{
                          fontSize: { xs: '12px', sm: '14px' }
                        }}
                      />
                      <FormControl
                        data-testid="unlock-channel-orgSetting"
                        size="small"
                        sx={{ minWidth: { xs: '45vw', sm: '25vw', md: '14.5vw' } }}
                      >
                        <Select
                          labelId="unlock-channel-multiple-chip-label"
                          id="unlock-channel-multiple-chip"
                          name="selectedUnlockChannel"
                          multiple
                          value={organisationDeviceData.selectedUnlockChannel}
                          onChange={handleChange}
                          IconComponent={ExpandMoreIcon}
                          sx={{ height: '35px', fontSize: { xs: '12px', sm: '14px' } }}
                          input={<OutlinedInput id="selectedDevice-multiple-chip" />}
                          renderValue={(selected) => selected.join(', ')}
                        >
                          {unlockChannelMethods.map((type) => (
                            <MenuItem
                              key={type}
                              value={type}
                              sx={{
                                m: 1,
                                p: 0,
                                fontSize: { xs: '12px', sm: '14px' },
                                '&:hover': {
                                  backgroundColor: 'transparent'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'transparent',
                                  '&:hover': {
                                    backgroundColor: 'transparent'
                                  }
                                },
                                '&:focus': {
                                  backgroundColor: 'transparent'
                                },
                                '&.Mui-selected.Mui-focusVisible': {
                                  backgroundColor: 'transparent'
                                }
                              }}
                              disableRipple
                              className={classes.menuItem}
                            >
                              <Checkbox
                                checked={organisationDeviceData.selectedUnlockChannel.includes(
                                  type
                                )}
                                size="small"
                                sx={{ color: 'transparent' }}
                                icon={
                                  CheckBoxOutlineIcon ? (
                                    <CheckBoxOutlineIcon
                                      style={{ height: '12px', width: '12px' }}
                                    />
                                  ) : null
                                }
                                checkedIcon={
                                  CheckedIcon ? (
                                    <CheckedIcon style={{ height: '12px', width: '12px' }} />
                                  ) : null
                                }
                              />
                              <ListItemText
                                primary={type}
                                sx={{
                                  fontSize: { xs: '12px', sm: '14px' },
                                  '& .MuiTypography-root': {
                                    fontSize: { xs: '12px', sm: '14px' },
                                    fontWeight: 400
                                  }
                                }}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </ListItem>

                    <ListItem
                      sx={{
                        p: { xs: 1, sm: 2 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'flex-start',
                        gap: 1,
                        backgroundColor: 'white'
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{
                          fontSize: { xs: '12px', sm: '14px' }
                        }}
                        secondaryTypographyProps={{
                          fontSize: { xs: '12px', sm: '14px' }
                        }}
                        primary="Low battery alarm limit"
                        secondary="Set minimum percentage (%) of battery at which the alarm is raised."
                      />
                      <FormControl
                        data-testid="low-battery-orgSetting"
                        size="small"
                        sx={{ minWidth: { xs: '30vw', sm: '14.5vw' }, mt: { xs: 0, sm: 2 } }}
                      >
                        {/* <InputLabel id="demo-simple-select-label">Battery Percentage</InputLabel> */}
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          name="lowBatteryThreshold"
                          value={organisationDeviceData.lowBatteryThreshold}
                          onChange={handleChange}
                          IconComponent={ExpandMoreIcon}
                          sx={{
                            height: '35px',
                            fontSize: { xs: '12px', sm: '14px' },
                            fontWeight: '500'
                          }}
                        >
                          <MenuItem className={classes.menuItem} value={5}>
                            5 %
                          </MenuItem>
                          <MenuItem className={classes.menuItem} value={10}>
                            10 %
                          </MenuItem>
                          <MenuItem className={classes.menuItem} value={15}>
                            15 %
                          </MenuItem>
                          <MenuItem className={classes.menuItem} value={20}>
                            20 %
                          </MenuItem>
                          <MenuItem className={classes.menuItem} value={25}>
                            25 %
                          </MenuItem>
                          <MenuItem className={classes.menuItem} value={30}>
                            30 %
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </ListItem>

                    <ListItem
                      sx={{
                        p: { xs: 1, sm: 2 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        backgroundColor: 'rgba(176, 185, 199, .1)'
                      }}
                    >
                      <Box>
                        <ListItemText
                          primaryTypographyProps={{
                            fontSize: { xs: '12px', sm: '14px' }
                          }}
                          secondaryTypographyProps={{
                            fontSize: { xs: '12px', sm: '14px' }
                          }}
                          primary="GeoFence radius"
                          secondary="Set the radius to anywhere between 10-10,000m"
                        />
                      </Box>

                      <FormControl
                        data-testid="geoFence-orgSetting"
                        size="small"
                        sx={{ minWidth: { xs: '30vw', sm: '14.5vw' }, mt: { xs: 0, sm: '5px' } }}
                      >
                        <TextField
                          size="small"
                          variant="outlined"
                          type="number"
                          name="geoFenceRadius"
                          value={organisationDeviceData.geoFenceRadius}
                          onChange={handleChange}
                          inputProps={{ min: 10, max: 10000 }}
                          sx={{
                            height: '35px',
                            '& .MuiInputBase-root': { height: '35px' },
                            minWidth: '14.5vw',
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '12px', sm: '14px', md: '16px' } // Small screen: 12px, Larger screens: 14px
                            }
                          }}

                          // label="GeoFence"
                        />
                      </FormControl>
                    </ListItem>

                    <ListItem
                      sx={{
                        p: { xs: 1, sm: 2 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        backgroundColor: 'white'
                      }}
                    >
                      <Box sx={{ display: 'flex' }}>
                        <SwitchButton
                          inputProps={{
                            'aria-label': 'ant design',
                            'data-testid': 'switch-check-box-orgSetting'
                          }}
                          handleChange={(e) => handleSwitchStatus(e, 'isShipmentDelayAllowed')}
                          value={organisationDeviceData.isShipmentDelayAllowed}
                        />
                        <ListItemText
                          primaryTypographyProps={{
                            fontSize: { xs: '12px', sm: '14px', md: '16px' }
                          }}
                          secondaryTypographyProps={{
                            fontSize: { xs: '12px', sm: '14px', md: '16px' }
                          }}
                          sx={{ pl: 2 }}
                          primary="Shipment delay allowance "
                          secondary="Set the minimum time of delay between 30-1440 mins  at which the alarm is raised."
                        />
                      </Box>
                      <TextField
                        size="small"
                        variant="outlined"
                        type="number"
                        sx={{
                          maxWidth: '4.5vw',
                          height: '35px',
                          '& .MuiInputBase-root': { height: '35px' },
                          minWidth: { xs: '30vw', sm: '14.5vw' },
                          mt: { xs: 0, sm: 2 },
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '12px', sm: '14px', md: '16px' }
                          }
                        }}
                        name="delayedTripThreshold"
                        value={organisationDeviceData.delayedTripThreshold}
                        onChange={handleChange}
                        inputProps={{ min: 30, max: 1440 }}
                        disabled={!organisationDeviceData.isShipmentDelayAllowed}
                        // label="Shipment delay allowance"
                      />
                    </ListItem>

                    <ListItem
                      sx={{
                        p: { xs: 1, sm: 2 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'flex-start',
                        gap: 1,
                        justifyContent: 'space-between',
                        backgroundColor: 'rgba(176, 185, 199, .1)'
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{
                          fontSize: { xs: '12px', sm: '14px', md: '16px' }
                        }}
                        secondaryTypographyProps={{
                          fontSize: { xs: '12px', sm: '14px', md: '16px' }
                        }}
                        primary="Server Data Update Frequency"
                        secondary="Set the server data update frequency between 30sec - 21600sec"
                      />
                      <FormControl
                        data-testid="notification-frequency-orgSetting"
                        size="small"
                        sx={{ minWidth: { xs: '30vw', sm: '14.5vw' }, mt: { xs: 0, sm: 2 } }}
                      >
                        <TextField
                          size="small"
                          variant="outlined"
                          type="number"
                          name="Notification Frequency"
                          value={organisationDeviceData.notificationFrequency}
                          onChange={handleChange}
                          inputProps={{ min: 30, max: 21600 }}
                          sx={{
                            height: '35px',
                            '& .MuiInputBase-root': { height: '35px' },
                            minWidth: '14.5vw',
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '12px', sm: '14px', md: '16px' }
                            }
                          }}
                        />
                      </FormControl>
                    </ListItem>

                    <ListItem
                      sx={{
                        p: { xs: 1, sm: 2 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'flex-start',
                        gap: 1,
                        backgroundColor: '#ffffff'
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{
                          fontSize: { xs: '12px', sm: '14px' }
                        }}
                        secondaryTypographyProps={{
                          fontSize: { xs: '12px', sm: '14px' }
                        }}
                        primary="PSM Frequency"
                        secondary="Set the Power Saving Mode data update frequency between 5min - 7 days"
                      />
                      <FormControl
                        data-testid="psm-orgSetting"
                        size="small"
                        sx={{ minWidth: { xs: '30vw', sm: '14.5vw' }, mt: { xs: 0, sm: 2 } }}
                      >
                        <Select
                          size="small"
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          name="psm_freq"
                          value={organisationDeviceData?.psm_freq}
                          onChange={handleChange}
                          IconComponent={ExpandMoreIcon}
                          sx={{
                            height: '35px',
                            minWidth: { xs: '30vw', sm: '14.5vw' },
                            fontSize: { xs: '12px', sm: '14px' }
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 180,
                                overflowY: 'auto'
                              }
                            }
                          }}
                        >
                          {PsmFrequencyData?.map((item) => (
                            <MenuItem
                              key={item.value}
                              value={item.value}
                              className={classes.menuItem}
                            >
                              {item.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </ListItem>

                    <ListItem
                      sx={{
                        p: { xs: 1, sm: 2 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'flex-start',
                        gap: 1,
                        backgroundColor: 'rgba(176, 185, 199, .1)'
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{
                          fontSize: { xs: '12px', sm: '14px' }
                        }}
                        primary="VIP Numbers"
                      />
                      <Autocomplete
                        multiple
                        size="small"
                        sx={{
                          minWidth: { xs: '30vw', sm: '14.5vw' },
                          fontSize: { xs: '12px', sm: '14px' },
                          fontWeight: '500'
                        }}
                        id="tags-filled"
                        options={[]}
                        freeSolo
                        name="vipNumbers"
                        value={organisationDeviceData.vipNumbers}
                        onChange={(_, value, reason) => {
                          if (reason === 'remove-option') {
                            // If user removes a number, clear errors
                            setVipNumberError(false);
                            setVipLengthError(false);
                          }

                          const filteredValues = value.filter((val) => /^\d{10}$/.test(val));

                          if (filteredValues.length > 4) {
                            setVipNumberError(true);
                            return;
                          }

                          if (value.length > 0 && !/^\d{10}$/.test(value[value.length - 1])) {
                            setVipLengthError(true);
                            return;
                          }

                          setVipLengthError(false);
                          setVipNumberError(false);

                          setOrganisationDeviceData((prevData) => ({
                            ...prevData,
                            vipNumbers: filteredValues
                          }));
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                              <Chip
                                variant="outlined"
                                size="small"
                                label={option}
                                key={key}
                                {...tagProps}
                              />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField
                            type="number"
                            size="small"
                            sx={{
                              '& .MuiInputBase-input': {
                                fontSize: { xs: '12px', sm: '14px' } // Adjust the font size for input
                              }
                            }}
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              'data-testid': 'vip-numbers-input-orgSetting'
                            }}
                            error={vipNumberError || vipLengthError}
                            helperText={getHelperText()}
                          />
                        )}
                      />
                    </ListItem>

                    <ListItem
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'flex-start',
                        gap: 1,
                        backgroundColor: '#ffffff'
                      }}
                    >
                      <Box sx={{ display: 'flex' }}>
                        <SwitchButton
                          inputProps={{
                            'aria-label': 'ant design',
                            'data-testid': 'power-check-box-orgSetting'
                          }}
                          handleChange={(e) => handleSwitchStatus(e, 'isPowerSwitchEnabled')}
                          value={organisationDeviceData.isPowerSwitchEnabled}
                          sx={{ m: 0 }}
                        />
                        <ListItemText
                          primaryTypographyProps={{
                            fontSize: { xs: '12px', sm: '14px' }
                          }}
                          primary="Power Switch enabled "
                          sx={{ pl: 2 }}
                          // secondary=""
                        />
                      </Box>
                    </ListItem>
                  </List>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1,
                      flexWrap: 'wrap',
                      mt: 3
                    }}
                  >
                    <Box>
                      <Button
                        size="small"
                        variant="outlined"
                        //  onClick={handleResetToDefault}
                      >
                        {isSmallScreen ? 'Reset' : 'Reset to Default'}
                      </Button>
                    </Box>
                    <Box>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ mr: 2, boxShadow: 'none' }}
                        onClick={() => setCreateUserStepNo('1')}
                      >
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ boxShadow: 'none' }}
                        type="submit"
                        onClick={() => handleSubmit()}
                      >
                        {data?.id ? 'Save Changes' : 'Add'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Form>
          </FormikProvider>
        </Box>
      </Modal>
    </Box>
  );
}
